/**
 * The one place a Meta browser conversion is fired.
 *
 * Components never call `fbq()` directly. Every conversion goes through the analytics
 * abstraction, which calls `emitMetaEvent()` here, which is the only module in the bundle that
 * touches `window.fbq`. That is what keeps the browser payload honest: this function takes an
 * event type and an event name and NOTHING else, so the analytics `eventParams` bag — which on
 * this funnel carries loan amount, income, DOB, SSN and bank data at various steps — has no path
 * into a request to Meta. `analytics.js` also mutates that bag in place, which is a second reason
 * not to hand it over.
 *
 * The `event_id` is NOT minted here. The server renders it into `window.__metaPixel__.eventIds`
 * and re-derives the same value when it queues the paired Conversions API copy, so Meta receives
 * one conversion twice under one id and deduplicates it. Double-counting is the failure that
 * matters — it teaches the optimiser the wrong thing and corrupts reported ROAS in a direction
 * nobody notices.
 *
 * Mirrored by app/Services/Meta/MetaBrowserEventMap.php; a test asserts the two agree.
 *
 * Nothing here throws. A missing pixel (flag off, cookie control on, ad blocker, Instant Form
 * visitor) is a normal state, not an error, and must never break the funnel step that fired it.
 */

/**
 * The analytics-driven allow-list. Funnel ENTRY is keyed on NEXT_SUCCESS rather than a page view:
 * page views are already covered by the base pixel, and a link-scanner prefetch would otherwise
 * report as a human entering the funnel.
 *
 * 'LeadgenV4LoanTerms' is the v4 landing step (validationKey 'v4-loan-terms'). Event types come
 * from validationKeyToEventType() in signup/leadgen-v4/mixins/step.js.
 */
const META_EVENTS = {
    LeadgenV4LoanTerms: {
        NEXT_SUCCESS: 'SignupStarted',
    },
};

/**
 * Conversions fired by an explicit emitMetaConversion() call rather than resolved from an
 * analytics event.
 *
 * SubmitApplication used to be mapped to LeadgenV4BankingAccount/NEXT_SUCCESS above, and that was
 * wrong twice. NEXT_SUCCESS fires when validateStep resolves, which is BEFORE submitForm() posts
 * the sale — so it reported applications that were never submitted, including ones the applicant
 * abandoned or that failed validation server-side. And the Plaid-eligible cohort never reaches
 * that step at all: the SSN step routes them to bank-connect, which ends in a window.location
 * redirect, so the largest and most valuable segment reported nothing.
 *
 * It is now fired from the two places a submission actually happens — submitForm()'s success
 * branch and BankConnect's, immediately before the widget opens — each paired with a server
 * dispatch in the same request.
 */
const DIRECT_CONVERSIONS = ['SubmitApplication'];

function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Resolve an analytics event to a Meta event name, or null when it is not a conversion.
 * Pure — no globals, no side effects.
 *
 * Own-property lookups only. A bare `META_EVENTS[eventType][eventName]` resolves inherited
 * `Object.prototype` members, so an event named `toString` would return a function and be fired at
 * Meta as an event name.
 */
export function metaEventFor(eventType, eventName) {
    if (!hasOwn(META_EVENTS, eventType)) {
        return null;
    }

    const forType = META_EVENTS[eventType];

    return hasOwn(forType, eventName) ? forType[eventName] : null;
}

/**
 * Fire the browser half of a deduplicated conversion, if this event is one.
 *
 * Silently no-ops when the event is not a conversion, when the pixel never loaded, when the page
 * rendered no config, or when no id exists for the event (an Instant Form visitor gets an empty
 * `eventIds`, because their conversions belong to the server-side milestone path).
 */
export function emitMetaEvent(eventType, eventName) {
    try {
        const metaEventName = metaEventFor(eventType, eventName);

        if (!metaEventName || typeof window === 'undefined' || typeof window.fbq !== 'function') {
            return;
        }

        const eventId = window.__metaPixel__?.eventIds?.[metaEventName];

        if (!eventId) {
            return;
        }

        // Empty params on purpose: the shared event_id is the entire payload contract, and no
        // applicant data is permitted to cross into the browser event.
        window.fbq('track', metaEventName, {}, { eventID: eventId });
    } catch (error) {
        if (console.debug) {
            console.debug('Meta pixel event failed to fire:', error.message);
        }
    }
}

/**
 * Fire a conversion the caller names directly, for the exits the analytics stream cannot express.
 *
 * Same contract as emitMetaEvent: the id comes from the server-rendered bag, the params bag is
 * empty, and nothing here throws. The name is checked against DIRECT_CONVERSIONS first — an
 * arbitrary string must not become an event name at Meta, and an own-property lookup on the id bag
 * is what stops `toString` resolving to a function.
 */
export function emitMetaConversion(metaEventName) {
    try {
        if (!DIRECT_CONVERSIONS.includes(metaEventName)) {
            return;
        }

        if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
            return;
        }

        const eventIds = window.__metaPixel__?.eventIds;

        if (!eventIds || !hasOwn(eventIds, metaEventName)) {
            return;
        }

        const eventId = eventIds[metaEventName];

        if (!eventId || typeof eventId !== 'string') {
            return;
        }

        window.fbq('track', metaEventName, {}, { eventID: eventId });
    } catch (error) {
        if (console.debug) {
            console.debug('Meta pixel conversion failed to fire:', error.message);
        }
    }
}

export default emitMetaEvent;
